<template>
  <div class="qualities-section">
    <h5 class="qualities-section__title">Qualities recognised</h5>

    <b-spinner v-if="loading" />
    <template v-else-if="qualities?.qualities?.length">
      <div class="qualities-section__main-chart">
        <div class="qualities-section__chart-header">
          <h6 class="qualities-section__chart-title">Company qualities recognised</h6>
          <b-form-checkbox v-if="hasDivisionData" v-model="splitByDivision" switch class="qualities-section__toggle">
            Split by division
          </b-form-checkbox>
        </div>

        <div class="qualities-section__chart-content">
          <div class="qualities-section__donut-container">
            <donut-chart
              :labels="chartLabels"
              :values="chartValues"
              :colors="chartColors"
              cutout="50%"
              :chart-options="mainChartOptions"
            />
          </div>
          <div class="qualities-section__legend">
            <div
              v-for="(quality, index) in displayQualities"
              :key="quality.quality_id"
              class="qualities-section__legend-item"
            >
              <span class="qualities-section__legend-color" :style="{ backgroundColor: getColor(index) }"></span>
              <span class="qualities-section__legend-label">{{ quality.quality_name }}</span>
              <span class="qualities-section__legend-value">{{ quality.percentage }}%</span>
            </div>
          </div>
        </div>

        <div v-if="splitByDivision && hasDivisions" class="qualities-section__division-filter">
          <select-dropdown v-model="selectedDivision" :options="divisionOptions" />
        </div>
      </div>

      <b-row class="qualities-section__breakdown-row">
        <b-col v-for="(quality, index) in displayQualities" :key="quality.quality_id" cols="12" sm="6" lg="3">
          <quality-breakdown-card :quality="quality" :color-index="index" :program-ids="programIds" :params="params" />
        </b-col>
      </b-row>
    </template>
    <div v-else class="qualities-section__empty">
      <p class="text-muted mb-0">No qualities data available.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import SelectDropdown from '@/components/SelectDropdown.vue';
import DonutChart from '@/components/charts/DonutChart.vue';
import QualityBreakdownCard from '@/pages/recognition/components/analytics/QualityBreakdownCard.vue';
import { PaletteColors } from '@/Theme';
import { getDatasetColor } from '@/utils/chart';
import useDivisionSelection from '@/state/composables/useDivisionSelection';

const props = defineProps({
  qualities: {
    type: Object,
    default: undefined,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  programIds: {
    type: Array,
    default: () => [],
  },
  params: {
    type: Object,
    default: () => ({}),
  },
});

const { divisions, hasDivisions } = useDivisionSelection();

const splitByDivision = ref(false);
const selectedDivision = ref(null);

const divisionOptions = computed(() => {
  const options = [{ value: null, text: 'All divisions' }];
  for (const division of divisions.value) {
    options.push({
      value: division.division_id,
      text: division.name,
    });
  }
  return options;
});

const hasDivisionData = computed(() => {
  return hasDivisions.value && props.qualities?.by_division != null;
});

const displayQualities = computed(() => {
  if (!props.qualities?.qualities) {
    return [];
  }

  // If split by division and a division is selected, use division-specific data
  if (splitByDivision.value && selectedDivision.value && props.qualities.by_division) {
    return props.qualities.by_division[selectedDivision.value] || props.qualities.qualities;
  }

  return props.qualities.qualities;
});

const chartLabels = computed(() => displayQualities.value.map(q => q.quality_name));
const chartValues = computed(() => displayQualities.value.map(q => q.count));
const chartColors = computed(() => displayQualities.value.map((_, i) => PaletteColors[getDatasetColor(i)]));

const getColor = index => PaletteColors[getDatasetColor(index)];

const mainChartOptions = {
  aspectRatio: 1,
  plugins: {
    legend: {
      display: false,
    },
  },
};
</script>

<style scoped lang="scss">
.qualities-section {
  margin-bottom: 1.5rem;

  &__title {
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--palette-color-default);
  }

  &__main-chart {
    background-color: var(--palette-color-base-white);
    border-radius: 0.25rem;
    box-shadow: var(--depth-2);
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  &__chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 1rem;
  }

  &__chart-title {
    font-weight: 600;
    margin: 0;
    color: var(--palette-color-default);
  }

  &__toggle {
    font-size: 0.875rem;
  }

  &__chart-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;

    @media (min-width: 768px) {
      flex-direction: row;
      justify-content: center;
    }
  }

  &__donut-container {
    width: 200px;
    height: 200px;
    flex-shrink: 0;

    @media (min-width: 768px) {
      width: 250px;
      height: 250px;
    }
  }

  &__legend {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__legend-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
  }

  &__legend-color {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 0.125rem;
    flex-shrink: 0;
  }

  &__legend-label {
    color: var(--palette-color-default);
    flex: 1;
  }

  &__legend-value {
    font-weight: 600;
    color: var(--palette-color-default);
    min-width: 2.5rem;
    text-align: right;
  }

  &__division-filter {
    margin-top: 1rem;
    max-width: 200px;
  }

  &__breakdown-row {
    --bs-gutter-x: 1rem;
    --bs-gutter-y: 1rem;
  }

  &__empty {
    background-color: var(--palette-color-base-white);
    border-radius: 0.25rem;
    box-shadow: var(--depth-2);
    padding: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 9.375rem;
  }
}
</style>
